<script setup lang="ts" generic="T">
import { ref, computed, useSlots } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import { useAutocompletePagination } from 'src/composables/useAutocompletePagination';
import type { PaginatedResult } from 'src/composables/useAutocompletePagination';
import { get } from 'lodash';

const props = withDefaults(defineProps<{
  modelValue: T | null;
  fetchFn: (query: string, page: number) => Promise<PaginatedResult<T> | null | undefined>;
  fetchDetailFn?: (id: string) => Promise<T>;
  optionLabel?: string | ((item: T) => string);
  placeholder?: string;
  scrollHeight?: string;
  showClear?: boolean;
  fluid?: boolean;
  debounceMs?: number;
  class?: string;
  invalid?: boolean;
}>(), {
  optionLabel: 'name',
  placeholder: 'Search...',
  scrollHeight: '250px',
  showClear: true,
  fluid: true,
  debounceMs: 300,
});

const emit = defineEmits<{
  'update:modelValue': [value: T | null];
  'item-select': [event: { value: T }];
  'clear': [];
}>();

const slots = useSlots();

defineSlots<{
  option(props: { option: T; index: number }): any;
  detail(props: { item: T; loading: boolean }): any;
}>();

const {
  suggestions,
  isLoading,
  onComplete,
  passThrough,
} = useAutocompletePagination<T>({
  fetchFn: props.fetchFn,
  debounceMs: props.debounceMs,
});

const acRef = ref<{ $el: HTMLElement } | null>(null);
const detailItem = ref<T | null>(null) as { value: T | null };
const detailLoading = ref(false);
const hideList = ref(false);

const mergedPt = computed(() => ({
  listContainer: {
    ...passThrough.listContainer,
    style: hideList.value ? 'display: none' : undefined,
  },
  overlay: hideList.value ? { style: 'overflow: hidden' } : undefined,
}));

function closePanel() {
  (acRef.value as any)?.hide?.();
}

function handleChevronClick(option: T) {
  hideList.value = true;
  detailItem.value = option;
  const optionId = (option as Record<string, any>)?.id;
  if (props.fetchDetailFn && optionId) {
    detailLoading.value = true;
    props.fetchDetailFn(optionId)
      .then((result) => {
        detailItem.value = result;
      })
      .catch(() => {
        // silently keep lite data on error
      })
      .finally(() => {
        detailLoading.value = false;
      });
  }
}

function handleDetailSelect() {
  if (detailItem.value) {
    emit('update:modelValue', detailItem.value);
    emit('item-select', { value: detailItem.value });
    closePanel();
  }
}

function handleDetailBack() {
  detailItem.value = null;
  detailLoading.value = false;
  // hideList stays true — list reappears only after leave animation completes
}

function handleTransitionAfterLeave() {
  hideList.value = false;
}

function handleHide() {
  detailItem.value = null;
  detailLoading.value = false;
  hideList.value = false;
}

function handleBlur(): void {
  if (typeof props.modelValue === 'string') {
    emit('update:modelValue', null);
  }
}
</script>

<template>
  <AutoComplete
    ref="acRef"
    :model-value="modelValue"
    :suggestions="suggestions"
    :option-label="(data) => typeof props.optionLabel === 'function' ? props.optionLabel(data) : get(data, props.optionLabel, '')"
    :loading="isLoading"
    :placeholder="placeholder"
    :scroll-height="scrollHeight"
    :pt="mergedPt"
    :complete-on-focus="true"
    :show-clear="showClear"
    :fluid="fluid"
    :invalid="invalid"
    :class="props.class"
    @update:model-value="emit('update:modelValue', $event)"
    @complete="onComplete"
    @item-select="emit('item-select', $event)"
    @clear="emit('clear')"
    @blur="handleBlur"
    @hide="handleHide"
  >
    <template #header>
      <Transition name="ac-slide" @after-leave="handleTransitionAfterLeave">
        <div v-if="detailItem" class="ac-detail-panel">
          <div class="ac-detail-header">
            <button class="action-btn" @click.stop="handleDetailBack">
              <i class="pi pi-chevron-left" style="font-size: 11px" />
            </button>
            <button class="action-btn action-btn-primary" @click.stop="handleDetailSelect">
              <i class="pi pi-check" style="font-size: 11px" />
              Select
            </button>
          </div>
          <div class="ac-detail-content">
            <slot name="detail" :item="(detailItem as T)" :loading="detailLoading" />
          </div>
        </div>
      </Transition>
    </template>
    <template v-if="$slots.option" #option="slotProps">
      <div
        style="display: flex; align-items: center; gap: 0.375rem; width: 100%;"
      >
        <div style="flex: 1; min-width: 0;">
          <slot name="option" v-bind="slotProps" />
        </div>
        <button
          v-if="slots.detail"
          class="ac-detail-chevron"
          @click.stop="handleChevronClick(slotProps.option)"
        >
          <i class="pi pi-chevron-right" style="font-size: 10px" />
        </button>
      </div>
    </template>
  </AutoComplete>
</template>

<style scoped>
.ac-detail-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.25rem;
  color: var(--p-surface-400);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
.ac-detail-chevron:hover {
  color: var(--p-primary-500);
  background: var(--p-primary-50);
}
:root[class*='app-dark'] .ac-detail-chevron:hover {
  color: var(--p-primary-400);
  background: color-mix(in srgb, var(--p-primary-500) 12%, transparent);
}
</style>
